import { Fragment, type ReactNode } from "react";
import { headingId, parseInline, type InlineNode } from "../domain/richText";
import type { ArticleBlock } from "../shared";

/**
 * Article prose, rendered from typed blocks.
 *
 * Every anchor here is a plain `<a>` rather than a router `Link`. That is
 * deliberate: this component is also rendered to static HTML at build time,
 * where no router exists, and a content page is one of the few places a full
 * page load is the better behaviour anyway.
 */

function renderNodes(nodes: readonly InlineNode[]): ReactNode {
  return nodes.map((node, index) => {
    switch (node.kind) {
      case "text":
        return <Fragment key={index}>{node.value}</Fragment>;
      case "thai":
        // The Thai typeface and the language tag land on exactly the characters
        // that need them, which is what makes a screen reader switch voice.
        return (
          <span className="prose__th" lang="th" key={index}>
            {node.value}
          </span>
        );
      case "strong":
        return <strong key={index}>{renderNodes(node.children)}</strong>;
      case "em":
        return <em key={index}>{renderNodes(node.children)}</em>;
      case "term":
        return (
          <span className="prose__term" key={index}>
            {renderNodes(node.children)}
          </span>
        );
      case "link":
        return (
          <a href={node.href} key={index}>
            {renderNodes(node.children)}
          </a>
        );
    }
  });
}

export function Inline({ text }: { text: string }) {
  return <>{renderNodes(parseInline(text))}</>;
}

function Block({ block }: { block: ArticleBlock }) {
  switch (block.kind) {
    case "paragraph":
      return (
        <p>
          <Inline text={block.text} />
        </p>
      );

    case "heading":
      return (
        <h2 id={headingId(block.text)}>
          <Inline text={block.text} />
        </h2>
      );

    case "subheading":
      return (
        <h3 id={headingId(block.text)}>
          <Inline text={block.text} />
        </h3>
      );

    case "list":
      return block.ordered ? (
        <ol className="prose__list">
          {block.items.map((item) => (
            <li key={item}>
              <Inline text={item} />
            </li>
          ))}
        </ol>
      ) : (
        <ul className="prose__list">
          {block.items.map((item) => (
            <li key={item}>
              <Inline text={item} />
            </li>
          ))}
        </ul>
      );

    case "table":
      return (
        <figure className="prose__table">
          <div className="prose__table-scroll">
            <table>
              {block.caption ? <caption>{block.caption}</caption> : null}
              <thead>
                <tr>
                  {block.columns.map((column) => (
                    <th scope="col" key={column}>
                      <Inline text={column} />
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {block.rows.map((row, rowIndex) => (
                  <tr key={rowIndex}>
                    {row.map((cell, cellIndex) =>
                      // The first column is the one an extraction keys on, so
                      // it is a row header rather than another data cell.
                      cellIndex === 0 ? (
                        <th scope="row" key={cellIndex}>
                          <Inline text={cell} />
                        </th>
                      ) : (
                        <td key={cellIndex}>
                          <Inline text={cell} />
                        </td>
                      ),
                    )}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </figure>
      );

    case "callout":
      return (
        <aside className="prose__callout">
          <strong>
            <Inline text={block.title} />
          </strong>
          <p>
            <Inline text={block.text} />
          </p>
        </aside>
      );
  }
}

export function ArticleBody({ blocks }: { blocks: readonly ArticleBlock[] }) {
  return (
    <div className="prose">
      {blocks.map((block, index) => (
        <Block block={block} key={index} />
      ))}
    </div>
  );
}
